import { useState, useCallback, useEffect } from 'react';
import * as suggestionService from '@/services/suggestion.service';

/**
 * Get the localStorage key for tracking shown prompts per conversation
 * @param {string} conversationId - The conversation ID
 * @returns {string} The localStorage key
 */
const getPromptStorageKey = (conversationId) => {
  return conversationId ? `show_prompt_messages_${conversationId}` : 'show_prompt_messages';
};

/**
 * Custom hook to manage prompt suggestions with various display conditions
 *
 * @param {Object} config - Customization configuration object
 * @param {string} conversationId - Current conversation ID
 * @param {boolean} isAnswerLoading - Whether an answer is currently loading
 * @param {boolean} isLeadGenMode - Whether lead generation mode is active
 * @param {boolean} interactingWithLiveAgent - Whether user is interacting with a live agent
 * @param {boolean} isLiveAgentResolved - Whether the live agent conversation is resolved
 * @param {boolean} pollResponse - Whether polling for responses is active
 * @param {boolean} hasStartedConversation - Whether a conversation has started
 * @param {boolean} showConsent - Whether consent UI is shown
 * @param {Function} scrollMessage - Function to scroll to latest message
 * @returns {Object} Prompt suggestion state and handlers
 */
export default function usePromptSuggestions({
  config,
  conversationId,
  isAnswerLoading,
  isLeadGenMode,
  interactingWithLiveAgent,
  isLiveAgentResolved,
  pollResponse,
  hasStartedConversation,
  showConsent,
  scrollMessage,
  isInApp = false,
}) {
  // Simplified state management
  const [suggestions, setSuggestions] = useState(config?.prompt_suggestions || []);
  const [shouldShow, setShouldShow] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasPendingMarkAsShown, setHasPendingMarkAsShown] = useState(false);

  // Extract configuration values for easier access
  const isEnabled = config?.suggested_prompts_enabled;
  const showOnce = config?.show_prompt_messages;
  const alwaysSamePrompts = config?.show_always_same_prompts;
  
  /**
   * Fetch new prompt suggestions from the API
   */
  const fetchSuggestions = useCallback(async (currentConversationId) => {
    if (!currentConversationId || !isEnabled) {
      return;
    }
    
    try {
      setIsLoadingSuggestions(true);
      setShouldShow(false);
      
      const response = await suggestionService.postSuggestions({
        kb_id: config?.kb_id,
        conversation_id: currentConversationId,
      });
      
      if (response.status === 200) {
        const newSuggestions = response.data.result;
                
        // If API returns empty suggestions, fall back to static prompts
        if (newSuggestions && newSuggestions.length > 0) {
          setSuggestions(newSuggestions);
        } else {
          // Fall back to static prompts from config
          setSuggestions(config?.prompt_suggestions || []);
        }
        
        setIsLoadingSuggestions(false);
        updateVisibility();
        
        // Single scroll attempt is sufficient
        if (scrollMessage) {
          setTimeout(() => scrollMessage(), 300);
        }
      } else {
        setSuggestions(config?.prompt_suggestions || []);
        setIsLoadingSuggestions(false);
        updateVisibility();
        
        if (scrollMessage) {
          setTimeout(() => scrollMessage(), 300);
        }
      }
    } catch (error) {
      setSuggestions(config?.prompt_suggestions || []);
      setIsLoadingSuggestions(false);
      updateVisibility();
      
      if (scrollMessage) {
        setTimeout(() => scrollMessage(), 300);
      }
    }
  }, [config?.kb_id, config?.prompt_suggestions, isEnabled, scrollMessage]);
  
  /**
   * Update prompt visibility based on all conditions
   */
  const updateVisibility = useCallback(() => {
    // Don't show if feature is disabled
    if (!isEnabled) {
      setShouldShow(false);
      return;
    }

    // Don't show during specific states
    // For lead gen: only hide prompts after user has started interacting, but NOT in app
    const shouldHideForLeadGen = isLeadGenMode && hasStartedConversation && !isInApp;

    if (isAnswerLoading || shouldHideForLeadGen || interactingWithLiveAgent || isLiveAgentResolved || pollResponse || showConsent || isLoadingSuggestions) {
      setShouldShow(false);
      return;
    }
    
    // If "always same prompts" is enabled, always show them (ignore "show once" logic)
    if (alwaysSamePrompts) {
      setShouldShow(true);
      return;
    }
    
    // Handle "show once" logic (only when NOT using always same prompts)
    if (showOnce) {
      // Make "show once" conversation-specific if we have a conversation ID
      const storageKey = conversationId
        ? `show_prompt_messages_${conversationId}`
        : 'show_prompt_messages';
      const hasShownBefore = localStorage.getItem(storageKey);
      const shouldShow = !hasShownBefore;
      setShouldShow(shouldShow);
    } else {
      setShouldShow(true);
      // Clean up any existing localStorage keys when "show once" is disabled
      if (localStorage.getItem('show_prompt_messages')) {
        localStorage.removeItem('show_prompt_messages');
      }
      // Also clean up conversation-specific keys if we have a conversation ID
      if (conversationId) {
        const conversationKey = `show_prompt_messages_${conversationId}`;
        if (localStorage.getItem(conversationKey)) {
          localStorage.removeItem(conversationKey);
        }
      }
    }
  }, [
    isEnabled,
    showOnce,
    alwaysSamePrompts,
    conversationId,
    isAnswerLoading,
    isLeadGenMode,
    hasStartedConversation,
    interactingWithLiveAgent,
    isLiveAgentResolved,
    pollResponse,
    showConsent,
    isLoadingSuggestions,
    isInApp,
    hasPendingMarkAsShown
  ]);
  
  /**
   * Mark prompts as shown (for "show once" functionality)
   */
  const markAsShown = useCallback(() => {
    // Don't mark as shown if using "always same prompts" - they should always be visible
    if (alwaysSamePrompts) {
      return;
    }
    
    if (showOnce) {
      // Use conversation-specific storage key if we have a conversation ID
      const storageKey = conversationId
        ? `show_prompt_messages_${conversationId}`
        : 'show_prompt_messages';
      localStorage.setItem(storageKey, 'true');
    }
    // Hide prompts immediately when marked as shown
    setShouldShow(false);
  }, [showOnce, alwaysSamePrompts, conversationId, shouldShow]);
  
  /**
   * Reset prompt state (for new conversations)
   */
  const resetPrompts = useCallback(() => {
    setSuggestions(config?.prompt_suggestions || []);
    setIsLoadingSuggestions(false);
    if (localStorage.getItem('show_prompt_messages')) {
      localStorage.removeItem('show_prompt_messages');
    }

    // Also clear the kb_id specific localStorage key used by SuggestedPromptsContainer
    if (config?.kb_id) {
      const kbSpecificKey = `suggested_prompts_shown_${config.kb_id}`;
      if (localStorage.getItem(kbSpecificKey)) {
        localStorage.removeItem(kbSpecificKey);
      }
    }

    updateVisibility();
  }, [config?.prompt_suggestions, config?.kb_id, showOnce, updateVisibility]);
  
  /**
   * Handle response completion - either fetch new prompts or use static ones
   */
  const onResponseComplete = useCallback((currentConversationId) => {
    
    if (!isEnabled) {
      return;
    }
    
    if (!alwaysSamePrompts) {
      fetchSuggestions(currentConversationId);
    } else {
      setSuggestions(config?.prompt_suggestions || []);
      updateVisibility();
      if (scrollMessage) {
        setTimeout(() => scrollMessage(), 300);
      }
    }
  }, [
    isEnabled, 
    alwaysSamePrompts, 
    fetchSuggestions, 
    config?.prompt_suggestions, 
    updateVisibility,
    scrollMessage
  ]);
  
  // Update visibility whenever dependencies change
  useEffect(() => {
    updateVisibility();
  }, [updateVisibility]);
  
  // Initial setup when config changes
  useEffect(() => {
    if (!hasStartedConversation) {
      setSuggestions(config?.prompt_suggestions || []);
      setIsLoadingSuggestions(false);
      updateVisibility();
    }
  }, [config?.prompt_suggestions, hasStartedConversation, updateVisibility]);
  
  // Scroll when prompts are shown
  useEffect(() => {
    if (shouldShow && scrollMessage) {
      setTimeout(() => scrollMessage(), 300);
    }
  }, [shouldShow, scrollMessage]);
  
  // Debug final state
  const returnObject = {
    suggestions,
    shouldShow,
    isLoadingSuggestions,
    markAsShown,
    resetPrompts,
    onResponseComplete
  };  
  return returnObject;
} 